import { campaigns, pageLocale, } from 'application/pageDataObject';
import { warn, } from 'application/console';
import { getCountryCode, } from 'providers/geolocationProvider';
import { isCampaignEnabled, } from 'config/campaignConfig';
import { body, classList, makeImage, node, } from 'utils/dom';
import { normalizeCountryCode, normalizeLocale, } from 'utils/campaign';


const SITE_HEADER_LOGO_CLASSNAME = '.site-header__logo';
const BASE_LOGO_MORPH_CLASS = 'logo-morph';

const hasIsCampaignEnabledProperty = ({
  isCampaignEnabled: isActiveCampaignEnabled,
}) => isActiveCampaignEnabled;

function getActiveCampaigns(){
  if (!campaigns || !campaigns.logo_morph || !campaigns.logo_morph.morphs) {
    return Promise.resolve([]);
  }
  const logoMorphCampaigns = campaigns.logo_morph.morphs;
  return isCampaignEnabled(campaigns.logo_morph)
    .then(
      (logoMorphFeature) => {
        return hasIsCampaignEnabledProperty(logoMorphFeature) &&
          Promise.all(logoMorphCampaigns.map(isCampaignEnabled)) || [];
      }
    )
    .then(function handleEnabledCampaigns(enabledCampaigns){
      const activeCampaigns = enabledCampaigns.filter(hasIsCampaignEnabledProperty);
      const onlyCampaigns = activeCampaigns.map(c => c.campaign);
      return onlyCampaigns;
    });
}

function getLogoImageByCurrentCountryCode(imageByCountryCodes, normalizedCurrentCountryCode){
  const countryCodesWithCurrentCountryCode = imageByCountryCodes.find(
    ({ countryCodes, }) => (
      countryCodes
        .map(normalizeCountryCode)
        .includes(normalizedCurrentCountryCode)
    )
  );
  if (countryCodesWithCurrentCountryCode) {
    return Promise.resolve(countryCodesWithCurrentCountryCode.image);
  }
  return Promise.reject(
    `cannot find logo image for country code '${ normalizedCurrentCountryCode }'`
  );
}

function getLogoByCountryCode(imageByCountryCodes){
  return getCountryCode()
    .then(normalizeCountryCode)
    .then(getLogoImageByCurrentCountryCode.bind(0, imageByCountryCodes));
}

function getLogoByLocale(imageByLocales){
  const localeNormalized = normalizeLocale(pageLocale);
  const countryCodesWithCurrentCountryCode = imageByLocales.find(group => {
    return group.locales
      .map(normalizeLocale)
      .includes(localeNormalized);
  });
  return countryCodesWithCurrentCountryCode ? countryCodesWithCurrentCountryCode.image : null;
}

function getLogoImageUrl(config){
  return new Promise(function(resolve, reject){
    const { image, imageByCountryCodes, imageByLocales, } = config;
    if (image) {
      return resolve(image);
    }
    if (imageByCountryCodes) {
      return getLogoByCountryCode(imageByCountryCodes)
        .then(resolve)
        .catch(reject);
    }
    if (imageByLocales) {
      const logoByLocale = getLogoByLocale(imageByLocales);
      if (logoByLocale) {
        return resolve(logoByLocale);
      }
      return reject('cannot find logo by locale');
    }
    return reject(`Can't find logo image configuration!`, {
      image, imageByCountryCodes, imageByLocales, config,
    });
  });
}

function handleLogoMorphErrors(e){
  warn('logo morph error', e);
}

function getClassNameByCampaignName(campaignName){
  return campaignName.match(/[a-zA-Z0-9]+/g).join('-');
}

function addCampaignClassesToBody(campaignConf){
  const { campaignName, customClassName, } = campaignConf;
  const campaignClassName = getClassNameByCampaignName(campaignName);
  const logoMorphClasses = [
    BASE_LOGO_MORPH_CLASS,
    `${ BASE_LOGO_MORPH_CLASS }--${ campaignClassName }`,
  ];
  if (customClassName) {
    logoMorphClasses.push(customClassName);
  }
  classList(body).add(...logoMorphClasses);
}

function preloadImage(logoUrl){
  return new Promise(function(resolve, reject){
    makeImage({
      onload: resolve.part(logoUrl),
      onerror: reject,
      src: logoUrl,
    });
  });
}

function applyLogoImage(logoUrl){
  const logoElement = node(SITE_HEADER_LOGO_CLASSNAME);
  if (!logoElement) {
    return warn('cannot find logo element for logo morph');
  }
  return (logoElement.style.backgroundImage = `url('${ logoUrl }')`);
}

function activateCampaigns(activeCampaigns){
  const numCampaigns = activeCampaigns.length;
  if (numCampaigns > 1) {
    warn('cannot run multiple logo morph campaigns! please fix the config');
    return;
  }
  if (numCampaigns === 0) {
    return;
  }
  const activeCampaignConf = activeCampaigns[0];
  getLogoImageUrl(activeCampaignConf)
    .then(preloadImage)
    .then(function addLogoImageToDom(logoUrl){
      applyLogoImage(logoUrl);
      addCampaignClassesToBody(activeCampaignConf);
    })
  .catch(handleLogoMorphErrors);
}

export default function initLogoMorph(){
  return getActiveCampaigns()
    .then(activateCampaigns)
    .catch(handleLogoMorphErrors);
}


// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/initializers/logoMorphInitializer.js